import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AreaGraphCard = ({ selectedArea }) => {
  const [data, setData] = useState([]);

  // Mapping areas to device IDs (same as TotalVisitsCards)
  const areaToDeviceMapping = {
    '6/F Lobby': 'CITYU-SDS-FF-01',
    '6/F Activity Room': 'CITYU-SDS-FF-02',
    '6/F Activity Rooms': 'CITYU-SDS-FF-02',
    '7/F Activity Room': 'CITYU-SDS-FF-03',
    '7/F Activity Rooms': 'CITYU-SDS-FF-03',
    // Floor-level mappings
    '6/F': ['CITYU-SDS-FF-01', 'CITYU-SDS-FF-02'],
    '7/F': ['CITYU-SDS-FF-03']
  };

  // Function to get relevant device IDs based on selected area
  const getRelevantDeviceIds = (selectedArea) => {
    console.log('Getting device IDs for area:', selectedArea);
    
    // Check if it's a direct mapping
    if (areaToDeviceMapping[selectedArea]) {
      const deviceIds = Array.isArray(areaToDeviceMapping[selectedArea]) 
        ? areaToDeviceMapping[selectedArea] 
        : [areaToDeviceMapping[selectedArea]];
      console.log('Found device IDs:', deviceIds);
      return deviceIds;
    }
    
    console.log('No device IDs found for area:', selectedArea);
    return [];
  };

  // Fetch visit data when selectedArea changes
  useEffect(() => {
    const fetchVisitData = async () => {
      const relevantDeviceIds = getRelevantDeviceIds(selectedArea);
      
      if (relevantDeviceIds.length === 0) {
        console.log('No relevant device IDs found');
        setData([]);
        return;
      }

      try {
        // Get current date and calculate week range
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        
        // Calculate start of current week (Sunday)
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        
        // Calculate end of current week (Saturday)
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        // Format dates for API (YYYY-MM-DD)
        const startDate = startOfWeek.toISOString().split('T')[0];
        const endDate = endOfWeek.toISOString().split('T')[0];
        
        console.log('Fetching data from:', startDate, 'to:', endDate);
        console.log('For device IDs:', relevantDeviceIds);
        
        const response = await fetch(
          `https://njs-01.optimuslab.space/city_u/optimus/visits?start_time=${startDate}&end_time=${endDate}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const visitData = await response.json();
        
        console.log('API Response - Full visit data:', visitData);
        
        // Filter data for the relevant devices
        const deviceData = visitData.filter(item => relevantDeviceIds.includes(item.Device_ID));
        
        console.log('Filtered device data:', deviceData);
        
        // Create week data structure with aggregation
        const weekData = [];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        for (let i = 0; i < 7; i++) {
          const date = new Date(startOfWeek);
          date.setDate(startOfWeek.getDate() + i);
          
          // Format date to match API format exactly: "DD Month YYYY, DayName"
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'long' });
          const year = date.getFullYear();

          // Get correct day name abbreviation
          const dayAbbreviations = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const dayName = dayAbbreviations[date.getDay()];

          const formattedDate = `${day} ${month} ${year}, ${dayName}`;

          console.log(`Day ${i}: Looking for date format:`, formattedDate);
          
          // Aggregate visits for all relevant devices on this date
          let totalVisits = 0;
          
          // Only show data up to current day
          if (i <= currentDay) {
            relevantDeviceIds.forEach(deviceId => {
              const dayData = deviceData.find(item => 
                item.Device_ID === deviceId && item.date === formattedDate
              );
              if (dayData) {
                console.log(`Found data for ${deviceId} on ${formattedDate}:`, dayData.Total_visits);
                totalVisits += dayData.Total_visits;
              } else {
                console.log(`No data found for ${deviceId} on ${formattedDate}`);
              }
            });
          }
          
          console.log(`Total visits for ${dayNames[i]}:`, totalVisits);
          
          weekData.push({
            day: dayNames[i],
            visits: totalVisits
          });
        }
        
        console.log('Final week data:', weekData);
        setData(weekData);
      } catch (error) {
        console.error('Error fetching visit data:', error);
        // Set empty data on error
        setData([
          { day: 'Sun', visits: 0 },
          { day: 'Mon', visits: 0 },
          { day: 'Tue', visits: 0 },
          { day: 'Wed', visits: 0 },
          { day: 'Thu', visits: 0 },
          { day: 'Fri', visits: 0 },
          { day: 'Sat', visits: 0 },
        ]);
      }
    };

    // Debounce the fetch function to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      fetchVisitData();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedArea]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          padding: '8px 12px'
        }}>
          <p style={{ color: '#000000', margin: '0 0 4px 0', fontWeight: '500' }}>
            {label}
          </p>
          <p style={{ color: '#e6cfe6', fontWeight: 800, margin: '0', fontSize: '14px' }}>
            Visits: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Area: <span className="font-bold text-gray-800">{selectedArea}</span>
      </h2>
      
      {/* Custom Legend */}
      <div className="flex justify-center mb-4">
        <div className="flex items-center gap-2">
          <div className="w-12 h-4 rounded-sm" style={{ backgroundColor: '#FAE9FB' }}></div>
          <span className="text-sm text-gray-600">Visits</span>
        </div>
      </div>
      
      {/* Debug info}
      {/* <div className="mb-4 text-xs text-gray-500">
        <p>Selected Area: {selectedArea}</p>
        <p>Device IDs: {getRelevantDeviceIds(selectedArea).join(', ')}</p>
        <p>Data points: {data.length}</p>
        <p>Total visits this week: {data.reduce((sum, item) => sum + item.visits, 0)}</p>
      </div>
       */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
            <YAxis 
              tickLine={false} 
              axisLine={false} 
              domain={[0, 'dataMax + 100']}
              allowDataOverflow={false}
            />
            <Tooltip 
              cursor={{ fill: 'rgba(239, 246, 255, 0.5)' }} 
              content={<CustomTooltip />}
            />
            <Bar 
              dataKey="visits" 
              fill="#FAE9FB" 
              stroke="#e6cfe6" 
              strokeWidth={1.5} 
              barSize={50} 
              radius={[4, 4, 0, 0]} 
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaGraphCard;